import { RequestStatusType, SortCondition, ZupRequestStatusType } from 'shared/enums';
import { TAbsentInfo } from 'shared/types/absence.types';
import { edpApiV1 } from './index';

export type TGetAllAbsenceRequest = {
  type: string;
  page?: number;
  size?: number;
  order?: SortCondition;
  allAvailableOrganizations: boolean;
  byId?: string;
  fio?: string;
  personnelNumber?: string;
  positionName?: string;
};

export type TCreateAbsenceRequest = {
  employeeId: number;
  startedAt: string;
  endedAt: string;
  comment: string;
};

export type TCreateAbsenceResponse = {
  id: number;
  created: string;
  startedAt: string;
  endedAt: string;
  status: {
    type: RequestStatusType;
    name: string;
  };
  zupRequestStatus: {
    type: ZupRequestStatusType;
    name: string;
  };
  hr: {
    id: number;
    name: string;
    lastName: string;
    middleName: string;
    email: string;
  };
  supervisorComment: string;
  supervisor: {
    id: number;
    name: string;
    middleName: string;
    lastName: string;
    personnelNumber: string;
    division: string;
    position: string;
  };
  employee: {
    id: number;
    name: string;
    middleName: string;
    lastName: string;
    personnelNumber: string;
    division: string;
    position: string;
  };
};

export type TGetAllAbsenceResponse = {
  data: TAbsentInfo[];
  pagination: {
    size: number;
    page: number;
    total: number;
  };
};

export type TGetSingleAbsenceResponse = {
  id: number;
  created: string;
  startedAt: string;
  endedAt: string;
  status: {
    type: string;
    name: string;
  };
  zupRequestStatus: {
    type: ZupRequestStatusType;
    name: string;
  } | null;
  supervisorComment: string;
  creator: {
    id: number;
    name: string;
    middleName: string;
    lastName: string;
    personnelNumber: string;
    division: string;
    position: string;
  };
  employee: {
    id: number;
    name: string;
    middleName: string;
    lastName: string;
    personnelNumber: string;
    division: string;
    position: string;
  };
  hr: {
    id: number;
    name: string;
    lastName: string;
    middleName: string;
    email: string;
  };
  hrComment: string | null;
};

type TTakeToWorkAbsenceResponse = {
  id: number;
  created: string;
  startedAt: string;
  endedAt: string;
  status: {
    type: string;
    name: string;
  };
  zupRequestStatus: {
    type: string;
    name: string;
  };
  supervisorComment: string;
  supervisor: {
    id: number;
    name: string;
    middleName: string;
    lastName: string;
    personnelNumber: string;
    division: string;
    position: string;
  };
  employee: {
    id: number;
    name: string;
    middleName: string;
    lastName: string;
    personnelNumber: string;
    division: string;
    position: string;
  };
  hr: {
    id: number;
    name: string;
    lastName: string;
    middleName: string;
    email: string;
  };
};

type TChangeResponsibleRequest = {
  requestId: number;
  hrId: number;
};

type TChangeResponsibleResponse = {
  id: number;
  created: string;
  startedAt: string;
  endedAt: string;
  status: {
    type: string;
    name: string;
  };
  zupRequestStatus: {
    type: string;
    name: string;
  };
  supervisorComment: string;
  supervisor: {
    id: number;
    name: string;
    middleName: string;
    lastName: string;
    personnelNumber: string;
    division: string;
    position: string;
  };
  employee: {
    id: number;
    name: string;
    middleName: string;
    lastName: string;
    personnelNumber: string;
    division: string;
    position: string;
  };
  hr: {
    id: number;
    name: string;
    lastName: string;
    middleName: string;
    email: string;
  };
};

export interface AbsenceUploadFileResponse {
  data: {
    tableId: number;
    name: string;
    middleName: string;
    lastName: string;
    personnelNumber: string;
    position: string;
    division: string;
    startedAt: string;
    description?: string;
    endedAt: string;
    errors?: {
      date: string | null;
      employee: string | null;
      duplicate: string | null;
      interval: string | null;
    };
  }[];
  errors: { length: string };
}

export type AbsenceImportRequest = {
  tableId: number;
  personnelNumber: string;
  startedAt: string;
  endedAt: string;
}[];

export interface AbsenceImportResponse {
  data: AbsenceUploadFileResponse['data'];
  status: 'OK' | 'ERROR';
}

type TRejectAbsence = {
  requestId: number;
};

type TApproveAbsence = {
  requestId: number;
};
type TActionArrayAbsence = {
  requestIds: number[];
};

export const absenceApi = edpApiV1.injectEndpoints({
  endpoints: build => ({
    getAllAbsence: build.query<TGetAllAbsenceResponse, TGetAllAbsenceRequest>({
      query: params => ({ url: '/v1/admin/absence/requests', method: 'GET', params }),
      providesTags: ['absence'],
    }),
    getSingleAbsence: build.query<TGetSingleAbsenceResponse, string>({
      query: requestId => ({ url: `/v1/admin/absence/requests/${requestId}`, method: 'GET' }),
      providesTags: ['absenceSingle'],
    }),
    createAbsence: build.mutation<TCreateAbsenceResponse, TCreateAbsenceRequest>({
      query: body => ({
        url: `/v1/admin/absence/requests`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['absence'],
    }),
    takeToWorkAbsence: build.mutation<TTakeToWorkAbsenceResponse, number>({
      query: requestId => ({
        url: `/v1/admin/absence/requests/${requestId}/take-to-work`,
        method: 'POST',
      }),
      invalidatesTags: ['absence'],
    }),
    changeAbsenceResponsible: build.mutation<TChangeResponsibleResponse, TChangeResponsibleRequest>({
      query: ({ requestId, ...body }) => ({
        url: `/v1/admin/absence/requests/${requestId}/change-responsible`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['absence', 'absenceSingle'],
    }),
    rejectAbsence: build.mutation<TGetSingleAbsenceResponse, TRejectAbsence>({
      query: ({ requestId, ...body }) => ({
        url: `/v1/admin/absence/requests/${requestId}/reject`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['absence', 'absenceSingle'],
    }),
    rejectArrayAbsence: build.mutation<
      {
        status: 'OK';
      },
      TActionArrayAbsence
    >({
      query: body => ({
        url: '/v1/admin/absence/requests/reject',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['absence', 'absenceSingle'],
    }),
    approveAbsence: build.mutation<TGetSingleAbsenceResponse, TApproveAbsence>({
      query: ({ requestId, ...body }) => ({
        url: `/v1/admin/absence/requests/${requestId}/approve`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['absence', 'absenceSingle'],
    }),
    approveArrayAbsence: build.mutation<
      {
        status: 'OK';
      },
      TActionArrayAbsence
    >({
      query: body => ({
        url: '/v1/admin/absence/requests/approve',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['absence', 'absenceSingle'],
    }),
    absenceDownload: build.query<string, { type: string; allAvailableOrganizations: boolean }>({
      query: payload => ({
        url: '/v1/admin/absence/requests/download-file',
        method: 'GET',
        params: payload,

        responseHandler: async response => {
          const link = document.createElement('a');
          link.download = 'absence';
          link.href = window.URL.createObjectURL(await response.blob());
          link.click();
        },
      }),
    }),
    absenceFileUpload: build.mutation<AbsenceUploadFileResponse, FormData>({
      query: body => ({
        url: '/v1/admin/absence/requests/upload-file',
        method: 'POST',
        formData: true,
        body,
      }),
    }),

    absenceImport: build.mutation<AbsenceImportResponse, AbsenceImportRequest>({
      query: body => ({
        url: '/v1/admin/absence/requests/validate-and-create',
        method: 'POST',
        invalidatesTags: ['absence', 'absenceSingle'],
        body,
      }),
    }),
  }),

  overrideExisting: false,
});

export const {
  useGetAllAbsenceQuery,
  useLazyGetAllAbsenceQuery,
  useGetSingleAbsenceQuery,
  useCreateAbsenceMutation,
  useLazyGetSingleAbsenceQuery,
  useTakeToWorkAbsenceMutation,
  useChangeAbsenceResponsibleMutation,
  useApproveAbsenceMutation,
  useRejectAbsenceMutation,
  useLazyAbsenceDownloadQuery,
  useAbsenceFileUploadMutation,
  useAbsenceImportMutation,
  useApproveArrayAbsenceMutation,
  useRejectArrayAbsenceMutation,
} = absenceApi;
